import { AnimatePresence, MotionConfig, motion } from "framer-motion"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import HotelCards from "./HotelCards"

import {
  BotIcon,
  ChatIcon,
  ClipIcon,
  CloseIcon,
  HeartIcon,
  MinusIcon,
  PercentIcon,
  PlaneIcon,
  SendIcon,
  ShieldIcon,
  StopIcon,
} from "./Icons"
import { useAiChat } from "../hooks/AiChatWidget"

const CONSENT_KEY = "ai_chat_consent"
const NEWSLETTER_KEY = "ai_chat_newsletter"

// тень + тонкая обводка inline, чтобы панель была видна на белом фоне сайта
const PANEL_SHADOW =
  "0 0 0 1px rgba(0,0,0,0.14), 0 20px 56px rgba(0,0,0,0.32), 0 4px 12px rgba(0,0,0,0.16)"

// максимальная высота поля ввода (px), дальше появляется скролл
const COMPOSER_MAX_HEIGHT = 140

const readConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY) === "1"
  } catch {
    return false
  }
}

/* ---------- animation presets ---------- */
const spring = { type: "spring", stiffness: 260, damping: 22 }
const fadeUp = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: spring } }
const stagger = (delay = 0, gap = 0.07) => ({
  hidden: {},
  show: { transition: { delayChildren: delay, staggerChildren: gap } },
})

/* ---------- Header ---------- */
const Header = ({ logoUrl, brandName, onMinimize, onClose }) => {
  const { t } = useTranslation()
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, ...spring }}
      className="flex items-center gap-3 border-b border-aichat-primary/20 bg-aichat-primary/10 px-4 py-3"
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-10 w-10 object-contain" />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-aichat-surface text-aichat-primary">
          <BotIcon />
        </div>
      )}
      <div className="min-w-0 flex-1 leading-tight">
        {brandName && (
          <div className="truncate text-lg font-bold text-aichat-primary">{brandName}</div>
        )}
        <div className="text-sm text-aichat-ink-muted">{t("ai_chat.assistant")}</div>
      </div>

      <span className="relative flex h-2.5 w-2.5">
        <motion.span
          className="absolute inset-0 rounded-full bg-aichat-online"
          animate={{ scale: [1, 2.4], opacity: [0.5, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
        />
        <span className="relative h-2.5 w-2.5 rounded-full bg-aichat-online" />
      </span>

      <motion.button
        type="button"
        onClick={onMinimize}
        aria-label={t("ai_chat.aria_minimize")}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.85 }}
        className="text-aichat-ink-muted hover:text-aichat-ink"
      >
        <MinusIcon />
      </motion.button>
      <motion.button
        type="button"
        onClick={onClose}
        aria-label={t("ai_chat.aria_close")}
        whileHover={{ scale: 1.15, rotate: 90 }}
        whileTap={{ scale: 0.85 }}
        className="text-aichat-ink-muted hover:text-aichat-ink"
      >
        <CloseIcon />
      </motion.button>
    </motion.div>
  )
}

/* ---------- Onboarding hero: самолёт влетает и парит ---------- */
const FLY_DURATION = 1.2

const Hero = ({ heroUrl }) => {
  if (heroUrl) {
    return (
      <motion.img
        src={heroUrl}
        alt=""
        initial={{ opacity: 0, scale: 0.85, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
        transition={{
          opacity: { duration: 0.4 },
          scale: spring,
          y: { duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 },
        }}
        className="mx-auto mb-2 max-h-[170px] min-h-0 flex-1 object-contain"
      />
    )
  }

  return (
    <div className="relative mx-auto mb-2 max-h-[150px] min-h-[70px] w-full flex-1 overflow-hidden text-aichat-primary">
      {/* след полёта */}
      <svg viewBox="0 0 300 140" className="absolute inset-0 h-full w-full" fill="none">
        <motion.path
          d="M0 140 Q 110 110 150 70"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeOpacity="0.35"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: FLY_DURATION, ease: "easeOut" }}
        />
      </svg>

      {/* облака */}
      <motion.span
        className="absolute left-[12%] top-[18%] h-4 w-14 rounded-full bg-aichat-primary/10"
        animate={{ x: [0, 14, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.span
        className="absolute bottom-[18%] right-[10%] h-3 w-10 rounded-full bg-aichat-primary/10"
        animate={{ x: [0, -12, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
      />

      {/* круг */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="aspect-square h-[85%] rounded-full bg-aichat-primary/10"
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, ...spring }}
        />
      </div>

      {/* самолёт */}
      <div className="absolute left-1/2 top-1/2 -ml-8 -mt-8 h-16 w-16">
        <motion.div
          className="h-full w-full"
          initial={{ x: -150, y: 70, rotate: 30, opacity: 0, scale: 0.6 }}
          animate={{
            // точки на кривой Безье (совпадает со следом); иконка lucide Plane смотрит носом на 45° вверх-вправо,
            // поэтому rotate = 45 - угол касательной к траектории
            x: [-150, -99, -58, -24, 0],
            y: [70, 54, 38, 19, 0],
            rotate: [30, 26, 20, 12, 0],
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: FLY_DURATION,
            ease: "linear",
            times: [0, 0.2, 0.42, 0.68, 1], // замедление к концу
            opacity: { duration: 0.3 },
            scale: { duration: 0.5 },
          }}
        >
          <motion.div
            className="flex h-full w-full items-center justify-center"
            animate={{ x: [0, 5, 0], y: [0, -5, 0], rotate: [0, 2, -2, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: FLY_DURATION }}
          >
            <PlaneIcon width={56} height={56} strokeWidth={1.75} />
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

/* ---------- Onboarding & consent ---------- */
const Checkbox = ({ checked, onChange, children }) => (
  <label className="flex cursor-pointer items-start gap-3 text-sm text-aichat-ink">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="peer sr-only"
    />
    <motion.span
      whileTap={{ scale: 0.85 }}
      animate={{ scale: checked ? [1, 1.2, 1] : 1 }}
      transition={{ duration: 0.25 }}
      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 transition-colors ${
        checked
          ? "border-aichat-primary bg-aichat-primary text-aichat-on-primary"
          : "border-aichat-line bg-aichat-surface"
      }`}
    >
      <svg
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <motion.path
          d="m5 12 5 5 9-10"
          initial={false}
          animate={{ pathLength: checked ? 1 : 0, opacity: checked ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        />
      </svg>
    </motion.span>
    <span>{children}</span>
  </label>
)

const Feature = ({ icon, label }) => (
  <motion.div
    variants={fadeUp}
    whileHover={{ y: -3 }}
    className="flex flex-1 flex-col items-center gap-1 rounded-card bg-aichat-surface-soft px-2 py-3 text-center text-[13px] leading-tight text-aichat-ink"
  >
    <span className="text-aichat-primary">{icon}</span>
    {label}
  </motion.div>
)

const Onboarding = ({ heroUrl, brandName, onStart }) => {
  const { t } = useTranslation()
  const [newsletter, setNewsletter] = useState(false)

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-5 pb-4 pt-2">
      <Hero heroUrl={heroUrl} />

      <motion.div
        variants={stagger(0.7)}
        initial="hidden"
        animate="show"
        className="flex shrink-0 flex-col"
      >
        <motion.h3
          variants={fadeUp}
          className="text-center text-2xl font-extrabold text-aichat-ink"
        >
          {t("ai_chat.welcome")}
        </motion.h3>
        <motion.p
          variants={fadeUp}
          className="mb-4 text-center text-lg font-semibold leading-snug text-aichat-ink"
        >
          {t("ai_chat.welcome_sub")}
        </motion.p>

        <motion.div variants={stagger(0, 0.08)} className="mb-4 flex gap-2">
          <Feature icon={<PlaneIcon />} label={t("ai_chat.feat_offers")} />
          <Feature icon={<ShieldIcon />} label={t("ai_chat.feat_support")} />
          <Feature icon={<HeartIcon />} label={t("ai_chat.feat_vacation")} />
        </motion.div>

        {/* спейсер вместо чекбокса рассылки (высота чекбокса 20px + mb-4), чтобы раскладка не сдвигалась */}
        <div aria-hidden className="mb-4 h-5 shrink-0" />

        <motion.div variants={fadeUp}>
          <motion.button
            type="button"
            onClick={() => onStart(newsletter)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className="flex w-full items-center justify-center gap-2 rounded-button bg-aichat-primary py-3 text-base font-semibold text-aichat-on-primary transition-colors hover:bg-aichat-primary-strong"
          >
            {t("ai_chat.start")}
            <motion.svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </motion.svg>
          </motion.button>
        </motion.div>

        <motion.p variants={fadeUp} className="mt-3 text-center text-xs text-aichat-ink-muted">
          🔒 {t("ai_chat.data_safe", { brand: brandName })}
        </motion.p>
      </motion.div>
    </div>
  )
}

/* ---------- Chat pieces ---------- */
const BotAvatar = () => (
  <motion.div
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ type: "spring", stiffness: 400, damping: 18 }}
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-aichat-primary/10 text-aichat-primary"
  >
    <BotIcon />
  </motion.div>
)

const TypingDots = () => (
  <div className="flex gap-1.5 pt-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-2.5 w-2.5 rounded-full bg-aichat-primary/50"
        animate={{ y: [0, -6, 0], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }}
      />
    ))}
  </div>
)

const QuickActions = ({ actions, onPick }) => (
  <motion.div
    variants={stagger(0.35, 0.07)}
    initial="hidden"
    animate="show"
    className="grid grid-cols-2 gap-2 pl-0 pt-2"
  >
    {actions.map((a) => (
      <motion.button
        key={a.label}
        type="button"
        variants={{
          hidden: { opacity: 0, scale: 0.8, y: 10 },
          show: { opacity: 1, scale: 1, y: 0, transition: spring },
        }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onPick(a.query || a.label)}
        className="flex min-w-0 items-center gap-2 rounded-full border border-aichat-primary/20 bg-aichat-surface px-2 py-1.5 text-left text-[14px] leading-tight text-aichat-ink transition-colors hover:border-aichat-primary hover:bg-aichat-primary/5"
      >
        {a.image ? (
          <img src={a.image} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        ) : a.offer ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-aichat-accent text-aichat-on-primary">
            <PercentIcon />
          </span>
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-aichat-primary/10 text-aichat-primary">
            <PlaneIcon width={20} height={20} />
          </span>
        )}
        <span className="line-clamp-2 min-w-0 break-words">{a.label}</span>
      </motion.button>
    ))}
  </motion.div>
)

const Message = ({ msg, quickActions, onPick, onSelectHotel }) => {
  const { t } = useTranslation()

  if (msg.role === "user") {
    return (
      <motion.div
        initial={{ opacity: 0, x: 24, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={spring}
        style={{ originX: 1 }}
        className="flex flex-col items-end"
      >
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-aichat-primary px-4 py-3 text-[15px] text-aichat-on-primary">
          {msg.text}
        </div>
        <span className="mt-1 text-xs text-aichat-ink-muted">{msg.time}</span>
      </motion.div>
    )
  }

  return (
    <div className="flex gap-2">
      <BotAvatar />
      <motion.div
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.08, ...spring }}
        className="min-w-0 flex-1"
      >
        <div className="inline-block max-w-full whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm bg-aichat-bubble px-4 py-3 text-[15px] leading-snug text-aichat-ink">
          {msg.error ? t("ai_chat.error") : msg.text}
        </div>
        <div className="mt-1 text-xs text-aichat-ink-muted">{msg.time}</div>
        {msg.hotels?.length > 0 && <HotelCards hotels={msg.hotels} onSelect={onSelectHotel} />}
        {msg.quickActions && quickActions?.length > 0 && (
          <QuickActions actions={quickActions} onPick={onPick} />
        )}
      </motion.div>
    </div>
  )
}

const Composer = ({ isLoading, onSend, onStop, onAttach }) => {
  const { t } = useTranslation()
  const [value, setValue] = useState("")
  const textareaRef = useRef(null)

  // авто-рост поля: подгоняем высоту под содержимое, но не выше COMPOSER_MAX_HEIGHT
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    const next = Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight > COMPOSER_MAX_HEIGHT ? "auto" : "hidden"
  }, [value])

  const submit = () => {
    if (!value.trim() || isLoading) return
    onSend(value.trim())
    setValue("")
  }

  const onKeyDown = (e) => {
    // Enter отправляет, Shift+Enter переносит строку; не мешаем IME-вводу
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, ...spring }}
      className="border-t border-aichat-line bg-aichat-surface px-3 pb-2 pt-3"
    >
      <div className="flex items-end gap-2 rounded-[26px] border border-aichat-line bg-aichat-surface px-3 py-1.5 shadow-sm transition-colors focus-within:border-aichat-primary">
        {onAttach && (
          <motion.button
            type="button"
            onClick={onAttach}
            aria-label={t("ai_chat.aria_attach")}
            whileHover={{ rotate: -15, scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex h-10 shrink-0 items-center text-aichat-ink-muted hover:text-aichat-ink"
          >
            <ClipIcon />
          </motion.button>
        )}
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("ai_chat.placeholder")}
          className="min-w-0 flex-1 resize-none whitespace-pre-wrap break-words border-0 bg-transparent py-2.5 text-[15px] leading-5 text-aichat-ink outline-none placeholder:text-aichat-ink-muted [scrollbar-width:thin]"
        />
        <AnimatePresence mode="wait" initial={false}>
          {isLoading ? (
            <motion.button
              key="stop"
              type="button"
              onClick={onStop}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              whileTap={{ scale: 0.93 }}
              transition={{ duration: 0.15 }}
              className="flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-aichat-danger/40 bg-aichat-danger-soft px-3 text-sm font-semibold text-aichat-danger"
            >
              <motion.span
                animate={{ scale: [1, 1.25, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="flex"
              >
                <StopIcon />
              </motion.span>
              {t("ai_chat.stop")}
            </motion.button>
          ) : (
            <motion.button
              key="send"
              type="button"
              onClick={submit}
              disabled={!value.trim()}
              aria-label={t("ai_chat.aria_send")}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: value.trim() ? 1 : 0.5, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              whileHover={value.trim() ? { scale: 1.08, x: 1 } : undefined}
              whileTap={value.trim() ? { scale: 0.88, x: 4 } : undefined}
              transition={{ duration: 0.15 }}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-aichat-primary text-aichat-on-primary transition-colors hover:bg-aichat-primary-strong"
            >
              <SendIcon />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      <p className="mt-2 text-center text-xs text-aichat-ink-muted">{t("ai_chat.footer")}</p>
    </motion.div>
  )
}

const ChatView = ({ endpoint, locale, brandName, quickActions, onAttach }) => {
  const { t } = useTranslation()
  const { messages, isLoading, send, stop } = useAiChat({
    endpoint,
    locale,
    greeting: t("ai_chat.greeting", { brand: brandName }),
  })
  const endRef = useRef(null)

  // шаблоны показываем только пока пользователь ничего не отправил
  const hasUserMessage = messages.some((m) => m.role === "user")

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages, isLoading])

  return (
    <>
      <div className="flex-1 space-y-4 overflow-y-auto overflow-x-hidden bg-aichat-surface px-4 py-4 [scrollbar-width:thin]">
        {messages.map((m) => (
          <Message
            key={m.id}
            msg={m}
            quickActions={hasUserMessage ? undefined : quickActions}
            onPick={send}
            onSelectHotel={(hotel) => send(t("ai_chat.book_hotel", { hotel: hotel.name }))}
          />
        ))}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              key="typing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6, transition: { duration: 0.12 } }}
              className="flex gap-2"
            >
              <BotAvatar />
              <div className="rounded-2xl rounded-tl-sm bg-aichat-bubble px-4 py-3 text-[15px] text-aichat-ink">
                {t("ai_chat.searching")}
                <TypingDots />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={endRef} />
      </div>
      <Composer isLoading={isLoading} onSend={send} onStop={stop} onAttach={onAttach} />
    </>
  )
}

/* ---------- Site theme colors ---------- */
const hexToRgb = (hex) => {
  const value = String(hex || "")
    .trim()
    .replace(/^#/, "")
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value
  if (!/^[0-9a-f]{6}$/i.test(full)) return null
  return `${parseInt(full.slice(0, 2), 16)} ${parseInt(full.slice(2, 4), 16)} ${parseInt(full.slice(4, 6), 16)}`
}

// Цвета сайта (hex из админки) -> RGB-тройки, которые читают цвета aichat-* в tailwind.
// Нет themeColors или они пустые: ничего не переопределяем, работает цвет текущей темы.
const themeVars = (themeColors) => {
  const colors = themeColors || {}
  const primary = hexToRgb(colors.buttonBgColor) || hexToRgb(colors.primaryColor)
  const strong = hexToRgb(colors.buttonHoverBgColor) || primary
  const onPrimary = hexToRgb(colors.buttonTextColor)
  const vars = {}
  if (primary) {
    vars["--aichat-primary"] = primary
    vars["--aichat-primary-soft"] = primary
  }
  if (strong) vars["--aichat-primary-strong"] = strong
  if (onPrimary) vars["--aichat-on-primary"] = onPrimary
  return vars
}

/* ---------- Root widget ---------- */
const AiChatWidget = ({
  endpoint = "/ai_chat/messages",
  locale,
  logoUrl,
  heroUrl,
  brandName,
  bottomOffset = 96, // px от низа экрана до кнопки открытия
  onConsent, // опционально: ({ newsletter }) => void
  quickActions, // опционально: [{ label, image?, query?, offer? }]
  defaultOpen = false,
  onAttach,
  themeColors, // опционально: { primaryColor, buttonBgColor, buttonTextColor, buttonHoverBgColor, ... }
}) => {
  const { t } = useTranslation()
  const actions = quickActions ?? [
    { label: t("ai_chat.quick_egypt") },
    { label: t("ai_chat.quick_turkey") },
    { label: t("ai_chat.quick_greece") },
    { label: t("ai_chat.quick_offers"), offer: true },
  ]
  const [open, setOpen] = useState(defaultOpen)
  const [consented, setConsented] = useState(readConsent)
  const [session, setSession] = useState(0) // меняем key при закрытии → чат сбрасывается

  const start = (newsletter) => {
    try {
      localStorage.setItem(CONSENT_KEY, "1")
      localStorage.setItem(NEWSLETTER_KEY, newsletter ? "1" : "0")
    } catch {}
    onConsent?.({ newsletter })
    setConsented(true)
  }

  const close = () => {
    setOpen(false)
    setSession((s) => s + 1)
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="contents" style={themeVars(themeColors)}>
        <AnimatePresence>
          {open && (
            <motion.div
              key="panel"
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20, transition: { duration: 0.15 } }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              style={{
                transformOrigin: "bottom right",
                bottom: bottomOffset + 72,
                maxHeight: `calc(100vh - ${bottomOffset + 72 + 24}px)`,
                boxShadow: PANEL_SHADOW,
              }}
              className="fixed right-4 z-[9999] flex h-[640px] w-[390px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-modal bg-aichat-surface"
            >
              <Header
                logoUrl={logoUrl}
                brandName={brandName}
                onMinimize={() => setOpen(false)}
                onClose={close}
              />
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={consented ? `chat-${session}` : "onboarding"}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.2 }}
                  className="flex min-h-0 flex-1 flex-col"
                >
                  {consented ? (
                    <ChatView
                      endpoint={endpoint}
                      locale={locale}
                      brandName={brandName}
                      quickActions={actions}
                      onAttach={onAttach}
                    />
                  ) : (
                    <Onboarding heroUrl={heroUrl} brandName={brandName} onStart={start} />
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={t("ai_chat.aria_open")}
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 18, delay: 0.3 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          style={{ bottom: bottomOffset }}
          className="fixed right-4 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-aichat-primary text-aichat-on-primary shadow-lg transition-colors hover:bg-aichat-primary-strong"
        >
          {!open && (
            <motion.span
              className="pointer-events-none absolute inset-0 rounded-full bg-aichat-primary"
              animate={{ scale: [1, 1.7], opacity: [0.4, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
            />
          )}
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={open ? "close" : "chat"}
              initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
              transition={{ duration: 0.18 }}
              className="relative flex"
            >
              {open ? <CloseIcon /> : <ChatIcon />}
            </motion.span>
          </AnimatePresence>
        </motion.button>
      </div>
    </MotionConfig>
  )
}

export default AiChatWidget
